import { forwardRef, useImperativeHandle } from 'react';
import { EditorContent } from '@tiptap/react';
import { useRichTextEditor } from './hooks/useRichTextEditor';
import EditorToolbar from './EditorToolbar/EditorToolbar';
import { EditorBubbleMenu } from './EditorBubbleMenu';
import { markdownToHtml } from './utils/markdownToHtml';
import type { RichTextEditorHandle } from './types/richTextEditorHandle';
import styles from './RichTextEditor.module.css';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  placeholder?: string;
  testId?: string;
  onFocus?: () => void;
  onBlur?: () => void;
}

export const RichTextEditor = forwardRef<
  RichTextEditorHandle,
  RichTextEditorProps
>(
  (
    { value, onChange, disabled = false, placeholder, testId, onFocus, onBlur },
    ref
  ) => {
    const editor = useRichTextEditor({
      value,
      onChange,
      disabled,
      placeholder,
      onFocus,
      onBlur,
    });

    useImperativeHandle(
      ref,
      () => ({
        insertMarkdown(markdown: string) {
          if (!editor || editor.isDestroyed || !editor.isEditable) return;
          editor.chain().focus().insertContent(markdownToHtml(markdown)).run();
        },
      }),
      [editor]
    );

    const isEmpty = !value || value === '<p></p>';
    const showToolbar = !disabled && editor;

    const containerClass = [
      styles.container,
      !disabled && styles.editable,
      disabled && styles.disabled,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={containerClass}>
        {showToolbar && <EditorToolbar editor={editor} />}
        <div className={styles.editorWrapper} data-testid={testId}>
          <EditorContent editor={editor} />
          {showToolbar && <EditorBubbleMenu editor={editor} />}
          {isEmpty && !editor?.isFocused && placeholder && (
            <div className={styles.placeholder}>{placeholder}</div>
          )}
        </div>
      </div>
    );
  }
);
